import React, { memo, useMemo } from 'react'
import CheckFilledIcon from 'transfix-icons/build/CheckFilledIcon'
import WarningFilledIcon from 'transfix-icons/build/WarningFilledIcon'
import { Spinner } from 'transfix-ui/components/Spinner'
import Box from 'transfix-ui/core/Box'
import { isShipmentReadyToOffer } from '../../../pages/shipment-details/components/ShipmentComplianceRequirements/ComplianceCoverReviews/utils'
import IconWithStatus from '../IconWithStatus'
import {
  ICarrierRequirementsQuery,
  useCarrierRequirementsQuery,
} from '../graphql/CarrierRequirements'
import CarrierRequirementList from './CarrierRequirementList'

interface ICarrierRequirementsStatusProps {
  carrierId: string
  shipmentId: string
  onCarrierRequirementsLoaded?: (data: ICarrierRequirementsQuery) => void
  /**
   * hides the header on Carrier Requiremsnt Title
   * @default false
   */
  hideHeader?: boolean
}

const CarrierRequirementsStatus = memo<ICarrierRequirementsStatusProps>(
  ({ carrierId, hideHeader = false, onCarrierRequirementsLoaded, shipmentId }) => {
    const { data, loading: isLoadingRequirements } = useCarrierRequirementsQuery({
      variables: {
        carrierId,
        shipmentId,
      },
      fetchPolicy: 'network-only',
      onCompleted: onCarrierRequirementsLoaded,
    })
    const { carrierComplianceMatch } = data || {}
    const { meetsRequirements = false, requirementsMatchResults = [], complianceCoverReview } =
      carrierComplianceMatch || {}
    const { status: complianceCoverReviewStatus } = complianceCoverReview || {}

    const isCarrierCompliant = isShipmentReadyToOffer(
      meetsRequirements,
      complianceCoverReviewStatus
    )

    const requirements = useMemo(() => {
      return requirementsMatchResults.filter(value => !value.valid)
    }, [requirementsMatchResults])

    return (
      <div>
        {!isLoadingRequirements && meetsRequirements !== undefined && (
          <Box mt={3}>
            {isCarrierCompliant && !hideHeader && (
              <IconWithStatus
                icon={CheckFilledIcon}
                text='Meets compliance requirements'
                color='info'
              />
            )}
            {!isCarrierCompliant && !hideHeader && (
              <IconWithStatus
                icon={WarningFilledIcon}
                text='Does not meet compliance requirements'
                color='warning'
              />
            )}
            {requirements.length > 0 && !isCarrierCompliant && (
              <CarrierRequirementList requirements={requirements} />
            )}
          </Box>
        )}
        {isLoadingRequirements && (
          <Box mt={3} position='relative' height={50}>
            <Spinner />
          </Box>
        )}
      </div>
    )
  }
)

export default CarrierRequirementsStatus
